import React, { lazy, Suspense } from "react";
import {
  Navigate,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import {
  AdminPanel,
  AuthLoadingScreen,
  ClientCompaniesSkeleton,
  EmpathyMappingSkeleton,
  HelpAndSupportSkeleton,
  OrganizationSkeleton,
  OrganizationUsersSkeleton,
} from "../components";
import { AuthPage } from "../components/auth/AuthPage";
import { ActiveContextProvider } from "../contexts/ActiveContextContext";
import { AuthProvider } from "../contexts/AuthContext";
import { LanguageProvider } from "../contexts/LanguageContext";
import DashboardLayout from "./layout/DashboardLayout";

// Page imports
import AdminCompanies from "@/pages/AdminCompanies";
import AdsAnalytics from "@/pages/AdsAnalytics";
import AdsList from "@/pages/AdsList";
import AdsManager from "@/pages/AdsManager";
import Analytics from "@/pages/Analytics";
import AutoResponder from "@/pages/AutoResponder";
import CampaignLeads from "@/pages/CampaignLeads";
import CampaignStrategy from "@/pages/CampaignStrategy";
import ContentAnalytics from "@/pages/ContentAnalytics";
import ContentList from "@/pages/ContentList";
import Dashboard from "@/pages/Dashboard";
import EbookList from "@/pages/EbookList";
import Posts from "@/pages/Posts";
import PostsAnalytics from "@/pages/PostsAnalytics";
import ScheduleManager from "@/pages/ScheduleManager";
import ScheduleSuggestions from "@/pages/ScheduleSuggestions";
import VideoStudio from "@/pages/VideoStudio";
import RevenueIntelligence from "@/pages/RevenueIntelligence";
import Leadflow from "@/pages/Leadflow";
import VirtusWhatsapp from "@/pages/VirtusWhatsapp";
import LeadB2b from "@/pages/LeadB2b";
import VirtusInsights from "@/pages/VirtusInsights";
import SiteBuilder from "@/pages/SiteBuilder";
import LaunchApp from "@/pages/LaunchApp";
import SettingsPage from "@/pages/Settings";
import UserProfile from "@/pages/UserProfile";

// Style imports
import "../styles/App.css";
import "../styles/components.css";
import { ProtectedRoute, PublicRoute } from "./routes";

// Lazy loaded components
const EmpathyMapping = lazy(() => import("@/pages/EmpathyMapping"));
const OrganizationPage = lazy(() => import("@/pages/Organization"));
const OrganizationUsers = lazy(() => import("@/pages/OrganizationUsers"));
const AcceptInvitation = lazy(() => import("@/pages/AcceptInvitation"));
const ClientCompanies = lazy(() => import("@/pages/ClientCompanies"));
const ClientCompanyDetail = lazy(() => import("@/pages/ClientCompanyDetail"));

// Utility Pages
const HelpAndSupport = lazy(() => import("@/pages/HelpAndSupport"));

const AppRoutes: React.FC = () => {
  return (
    <Routes>
      {/* Telas de autenticação: uma URL por tela (o AuthPage lê o caminho) */}
      {[
        "/auth",
        "/auth/register",
        "/auth/forgot-password",
        "/auth/reset-password",
        "/auth/confirm-email",
      ].map((path) => (
        <Route
          key={path}
          path={path}
          element={
            <PublicRoute>
              <AuthPage />
            </PublicRoute>
          }
        />
      ))}

      {/* Convite de organização: acessível logado ou não (a própria página trata os dois casos) */}
      <Route
        path="/auth/invite/:token"
        element={
          <Suspense
            fallback={<AuthLoadingScreen message="Abrindo convite..." />}
          >
            <AcceptInvitation />
          </Suspense>
        }
      />

      <Route
        path="/"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Dashboard />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Rotas de Conteúdo */}
      <Route
        path="/content"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ContentList />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/content/ebooks"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <EbookList />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/content/insights"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ContentAnalytics />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/posts"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Posts />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/posts/insights"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <PostsAnalytics />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/posts/video-studio"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <VideoStudio />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/posts/ai-schedule"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ScheduleSuggestions />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/posts/calendar"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <ScheduleManager />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Rota unificada de campanhas (Meta + Google) */}
      <Route
        path="/ads/new"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AdsManager />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/ads"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AdsList />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Gestão de leads de campanhas (Funcionalidade nativa SMM) */}
      <Route
        path="/leads"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CampaignLeads />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/ads/insights"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AdsAnalytics />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/marketing-strategy"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <CampaignStrategy />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Rota de Help & Support */}
      <Route
        path="/support"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<HelpAndSupportSkeleton />}>
                <HelpAndSupport />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/analytics"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Analytics />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/credits"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <SettingsPage />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <UserProfile />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/auto-responder"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <AutoResponder />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      <Route
        path="/empathy-mapping"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<EmpathyMappingSkeleton />}>
                <EmpathyMapping />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Organização e Empresas Cliente */}
      <Route
        path="/organization"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<OrganizationSkeleton />}>
                <OrganizationPage />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/organization/users"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<OrganizationUsersSkeleton />}>
                <OrganizationUsers />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/organization/clients"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<ClientCompaniesSkeleton />}>
                <ClientCompanies />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/organization/clients/:id"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Suspense fallback={<OrganizationSkeleton />}>
                <ClientCompanyDetail />
              </Suspense>
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Rota admin - apenas para administradores */}
      <Route
        path="/admin"
        element={
          <ProtectedRoute adminOnly={true}>
            <DashboardLayout>
              <AdminPanel />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Rota admin tenants - gerenciamento de empresas (superusuário Virtus) */}
      <Route
        path="/admin/companies"
        element={
          <ProtectedRoute adminOnly={true}>
            <DashboardLayout>
              <AdminCompanies />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Revenue Intelligence */}
      <Route
        path="/revenue-intelligence"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <RevenueIntelligence />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Leadflow */}
      <Route
        path="/apps/leadflow"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <Leadflow />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Virtus Chat (CRM de atendimento no WhatsApp) */}
      <Route
        path="/apps/chat"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <VirtusWhatsapp />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Virtus Lead B2B */}
      <Route
        path="/apps/lead-b2b"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <LeadB2b />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Virtus Insights */}
      <Route
        path="/apps/insights"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <VirtusInsights />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Virtus Site Builder */}
      <Route
        path="/apps/site-builder"
        element={
          <ProtectedRoute>
            <DashboardLayout>
              <SiteBuilder />
            </DashboardLayout>
          </ProtectedRoute>
        }
      />

      {/* Ponte de SSO do menu de aplicações: /launch/<app> abre a aplicação com login automático */}
      <Route
        path="/launch/:app"
        element={
          <ProtectedRoute>
            <LaunchApp />
          </ProtectedRoute>
        }
      />

      {/* Qualquer outra rota redireciona para auth */}
      <Route path="*" element={<Navigate to="/auth" replace />} />
    </Routes>
  );
};

const App: React.FC = () => {
  return (
    <Router>
      <LanguageProvider>
        <AuthProvider>
          <ActiveContextProvider>
            <div className="App">
              <AppRoutes />
            </div>
          </ActiveContextProvider>
        </AuthProvider>
      </LanguageProvider>
    </Router>
  );
};

export default App;
